/**
 * WordPress reads for every editorial surface — the header's Coverage counts and the
 * coverage pages themselves. Server-side only: these endpoints are not CORS-open, and
 * the fan-out belongs behind a cache.
 *
 * The contract this file leans on, verified live 2026-07-30 against the `api` namespace:
 *   - `tax_posts?tax[topic][]=A&tax[topic][]=B` → **OR** within one taxonomy
 *   - `…&tax[category]=X`                        → **AND** across taxonomies
 *   - `…&tax[!category]=X`                       → negation
 *   - every response carries `total_posts`, newest-first
 * Proof of the OR/AND split: topics 27275+27274 = 557, ∩ reviews = 261 = 217 + 44.
 *
 * Term ids are resolved from slugs at runtime and memoised (see `config.ts` for why they
 * are never hardcoded). All three taxonomies (`category`, `post_tag`, `topic`) are
 * REST-exposed under `/wp/v2` — `topic` since ask 2 of docs/wp-endpoint-edit-request.md
 * shipped (verified live 2026-08-04).
 */

import { publicConfig } from 'config'
import { authorLinkBuilder, postLinkBuilder } from 'ds/mappers/link-wrappers'
import { decodeEntities } from 'lib/decode-entities'
import { SimpleCache } from 'shared-code/simple-cache'
import type { EditorialStory } from './types'

/**
 * WP is slow and this runs off the render path, but a hung upstream must still end.
 * 15 s rather than 9 s: at 9 s a handful of Coverage rows aborted on every build and
 * came back `null`, which reads as "no data" in a panel that had data yesterday.
 * Concurrency is capped in `fetch.ts` for the same reason — WP degrades under fan-out.
 */
const REQUEST_TIMEOUT_MS = 15_000

/**
 * Term ids only change when an editor recreates a term — memoise for a day. Only
 * successful lookups are stored, so a transient failure is retried on the next build
 * instead of pinning a whole tab to "no counts" until the process restarts.
 */
const ID_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const idCache = new SimpleCache<number>(ID_CACHE_TTL_MS)

interface RawPost {
  ID: number
  slug: string
  title: string
  excerpt: string | null
  image: { src?: string | null; srcset?: string | null; webp_srcset?: string | null } | null
  /** Award-badge image object (Editor's Choice / Recommended) — reviews mostly. */
  badge: { src?: string | null } | null
  date: string | null
  score: number | null
  category: string | null
  author_name: string | null
  author_slug: string | null
  /** WP avatar — Gravatar at `s=96&d=mm` (verified live 2026-09-25). */
  author_avatar: { src?: string | null } | null
}

interface RawList {
  posts?: RawPost[]
  total_posts?: number
  total_pages?: number
}

function apiBase(): string {
  const { baseUrl } = publicConfig('wpApiConfig')
  if (!baseUrl) {
    throw new Error('Config error, wpApiConfig.baseUrl is undefined')
  }
  return baseUrl.replace(/\/$/, '')
}

/** `…/wp-json/api` → `…/wp-json/wp/v2` — the core namespace, for term lookups. */
function coreBase(): string {
  return apiBase().replace(/\/api$/, '/wp/v2')
}

/**
 * One WP read, with the two failure kinds kept apart: `unreachable` (network, timeout, 5xx,
 * HTML error page) versus a well-formed answer. Most callers collapse both to `null`
 * (`getJson`); the tree loader must not — "WordPress said no such term" drops a type from
 * the fallback tree, "WordPress did not answer" keeps it as not-yet-known.
 */
type WpRead<T> = { ok: true; body: T; status: number } | { ok: false; status: number | null }

async function readJson<T>(url: string, timeoutMs = REQUEST_TIMEOUT_MS): Promise<WpRead<T>> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl.signal })
    if (!res.ok) {
      return { ok: false, status: res.status }
    }
    return { ok: true, body: (await res.json()) as T, status: res.status }
  } catch {
    return { ok: false, status: null }
  } finally {
    clearTimeout(timer)
  }
}

async function getJson<T>(url: string, timeoutMs = REQUEST_TIMEOUT_MS): Promise<T | null> {
  const read = await readJson<T>(url, timeoutMs)
  // Every caller treats null as "no number available" and renders the row bare.
  return read.ok ? read.body : null
}

/** The `api` namespace wraps its payloads in `{ data }`; core `/wp/v2` does not. */
function unwrap<T>(body: { data?: T } | T | null): T | null {
  if (body === null) {
    return null
  }
  return (body as { data?: T }).data ?? (body as T)
}

/** Tag (`post_tag`) — REST-exposed like categories, so the same cheap lookup. */
export async function resolveTagId(slug: string): Promise<number | null> {
  return resolveTermId('tag', 'tags', slug)
}

/** Editorial category (`category` taxonomy) — REST-exposed, so one cheap lookup. */
export async function resolveCategoryId(slug: string): Promise<number | null> {
  return resolveTermId('category', 'categories', slug)
}

/** A slug lookup's outcome: the id, `null` for "no such term", `undefined` for "WP did not answer". */
export type TermLookup = number | null | undefined

async function lookupTermId(
  kind: 'category' | 'tag' | 'topic',
  restBase: string,
  slug: string
): Promise<TermLookup> {
  const key = `${kind}:${slug}`
  const hit = idCache.get(key)
  if (hit !== null) {
    return hit
  }
  const read = await readJson<{ id: number }[]>(
    `${coreBase()}/${restBase}?slug=${encodeURIComponent(slug)}&_fields=id`
  )
  if (!read.ok || !Array.isArray(read.body)) {
    return undefined
  }
  const id = read.body[0]?.id
  if (typeof id !== 'number') {
    return null
  }
  idCache.set(key, id)
  return id
}

async function resolveTermId(
  kind: 'category' | 'tag' | 'topic',
  restBase: string,
  slug: string
): Promise<number | null> {
  return (await lookupTermId(kind, restBase, slug)) ?? null
}

/** Product topic (`topic` taxonomy) — REST-exposed since ask 2 shipped, same cheap lookup. */
export async function resolveTopicId(slug: string): Promise<number | null> {
  return resolveTermId('topic', 'topic', slug)
}

/**
 * Same lookup, failure kinds kept apart — for the fallback tree, where "no such term"
 * (the type is not on this install) and "no answer" (keep the type, ids unknown) must
 * not be the same `null`.
 */
export async function lookupTopicId(slug: string): Promise<TermLookup> {
  return lookupTermId('topic', 'topic', slug)
}

/**
 * The raw reviews tree WordPress serves — `GET api/editorial-tree` (WP request v6, Ask 3).
 * Edge-cached an hour and purged on every topic edit and registry save. Unvalidated: the
 * loader (`lib/editorial/tree.ts`) decides whether to trust it. `undefined` = no answer
 * (unreachable, or the route is not deployed on this install: 404); the loader memoises
 * that outcome for its fresh window so an absent endpoint costs one probe per cycle.
 */
export async function fetchEditorialTreeRaw(timeoutMs?: number): Promise<unknown> {
  const read = await readJson<unknown>(`${apiBase()}/editorial-tree`, timeoutMs)
  return read.ok ? unwrap(read.body as { data?: unknown } | null) : undefined
}

export interface TopTag {
  id: number
  slug: string
  /** WP term name, entities decoded ("Bowers & Wilkins"). */
  name: string
  /** Equals `tax_posts` `total_posts` for the same topic set + tag (WP decisions #1). */
  count: number
}

/** `api/tax_top_tags` takes at most this many ids per taxonomy; past it the call is a 400. */
const TOP_TAGS_MAX_IDS = 20

/**
 * The most-used `post_tag`s inside a topic set ∩ category, ranked by count — one call
 * replaces the old 25 hand-picked candidates × `tax_counts` (WP request v6, Ask 5). Each
 * `count` is re-read through the listing's own count path, so the number on a pill and
 * the list it opens agree. `null` = no answer or a malformed body; callers render no
 * tag pills rather than guessing.
 */
export async function fetchTopTags(query: {
  topicIds: number[]
  categoryId: number
  limit: number
  min: number
}): Promise<TopTag[] | null> {
  const qs = new URLSearchParams({ limit: String(query.limit), min: String(query.min) })
  // The endpoint caps ids per taxonomy; a scope past the cap would 400 for the whole call.
  // Ranking over the first N is honest enough for a pill row and cannot happen today
  // (the widest scope carries 4 term ids) — warn so a grown tree is noticed.
  const topicIds = query.topicIds.slice(0, TOP_TAGS_MAX_IDS)
  if (topicIds.length < query.topicIds.length) {
    warnOnce(
      'top-tags:ids-capped',
      `[editorial-tree] tax_top_tags takes ${TOP_TAGS_MAX_IDS} topic ids; a scope has ${query.topicIds.length} — ranking over the first ${TOP_TAGS_MAX_IDS}`
    )
  }
  for (const id of topicIds) {
    qs.append('tax[topic][]', String(id))
  }
  qs.append('tax[category][]', String(query.categoryId))
  const body = unwrap(
    await getJson<{ data?: { tags?: unknown } } | { tags?: unknown }>(
      `${apiBase()}/tax_top_tags?${qs.toString()}`
    )
  )
  const tags = body?.tags
  if (!Array.isArray(tags)) {
    return null
  }
  const result: TopTag[] = []
  for (const tag of tags as unknown[]) {
    const t = tag as Partial<TopTag> | null
    if (
      t &&
      typeof t.id === 'number' &&
      typeof t.slug === 'string' &&
      typeof t.count === 'number'
    ) {
      result.push({
        id: t.id,
        slug: t.slug,
        name: decodeEntities(typeof t.name === 'string' ? t.name : t.slug),
        count: t.count,
      })
    }
  }
  return result
}

const warned = new Set<string>()

/** One line per distinct problem per process — a repeated warning is noise, a first one is a signal. */
export function warnOnce(key: string, message: string): void {
  if (warned.has(key)) {
    return
  }
  warned.add(key)
  // eslint-disable-next-line no-console
  console.warn(message)
}

export interface TaxQuery {
  /** OR-ed together. */
  topicIds: number[]
  /** AND-ed with the topics. */
  categoryId?: number | null
  /** Excluded (e.g. drop News from a "latest" list). */
  excludeCategoryId?: number | null
  /** AND-ed too — one brand/platform tag, for the coverage pages' filter pills. */
  tagId?: number | null
  perPage: number
  /** 1-based. */
  page?: number
}

function taxPostsUrl({
  topicIds,
  categoryId,
  excludeCategoryId,
  tagId,
  perPage,
  page = 1,
}: TaxQuery): string {
  const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) })
  for (const id of topicIds) {
    qs.append('tax[topic][]', String(id))
  }
  if (categoryId) {
    qs.set('tax[category]', String(categoryId))
  }
  if (excludeCategoryId) {
    qs.set('tax[!category]', String(excludeCategoryId))
  }
  if (tagId) {
    qs.set('tax[post_tag]', String(tagId))
  }
  return `${apiBase()}/tax_posts?${qs.toString()}`
}

export interface ListResult {
  total: number | null
  stories: EditorialStory[]
  /** Total pages at the requested page size — drives the coverage pages' pagination. */
  totalPages: number | null
}

const EMPTY_LIST: ListResult = { total: null, stories: [], totalPages: null }

/** Posts at the intersection of a topic set and (optionally) an editorial category. */
export async function fetchTaxPosts(query: TaxQuery): Promise<ListResult> {
  // A query with neither axis would return the whole site — refuse rather than mislead.
  if (query.topicIds.length === 0 && !query.categoryId) {
    return EMPTY_LIST
  }
  const raw = unwrap(await getJson<{ data?: RawList } | RawList>(taxPostsUrl(query)))
  return toListResult(raw, query.perPage)
}

/** A count-only query — same axes as `TaxQuery`, no pagination. */
export type TaxCountQuery = Omit<TaxQuery, 'perPage' | 'page'>

/**
 * Totals for a list of tax queries, positions preserved (`null` = unresolvable).
 *
 * ONE `api/tax_counts` request for the whole list (ask 1 of
 * docs/wp-endpoint-edit-request.md, verified live 2026-08-04; up to 30 items per call).
 * A failed or malformed response yields all-null totals — callers render those rows
 * bare, same as any other missing number.
 */
/** `api/tax_counts` contract: up to 30 items per call (docs/wp-endpoint-edit-request.md). */
const TAX_COUNTS_MAX_ITEMS = 30

export async function fetchTaxTotals(queries: TaxCountQuery[]): Promise<(number | null)[]> {
  const totals: (number | null)[] = queries.map(() => null)
  // Same refusal as fetchTaxPosts: a query with neither axis would count the whole site.
  const valid = queries
    .map((query, index) => ({ query, index }))
    .filter(({ query }) => query.topicIds.length > 0 || query.categoryId)
  if (valid.length === 0) {
    return totals
  }
  // The endpoint takes at most 30 items per call; past that it answers with an HTML
  // error page and the whole batch came back null (the hub root's pills — 1 scope total
  // + 7 scopes + 25 tags — hit this on 2026-09-26). Chunks run in SERIES: WP drops
  // requests under fan-out.
  for (let start = 0; start < valid.length; start += TAX_COUNTS_MAX_ITEMS) {
    const chunk = valid.slice(start, start + TAX_COUNTS_MAX_ITEMS)
    const batched = await fetchTaxCountsBatch(chunk.map(v => v.query))
    if (batched) {
      chunk.forEach((v, i) => {
        totals[v.index] = batched[i] ?? null
      })
    }
  }
  return totals
}

async function fetchTaxCountsBatch(queries: TaxCountQuery[]): Promise<(number | null)[] | null> {
  // Mirrors `tax_posts` semantics: OR within a taxonomy's id list, AND across
  // taxonomies, `!` negates. The contract lives in docs/wp-endpoint-edit-request.md.
  const payload = queries.map(q => {
    const tax: Record<string, number[]> = {}
    if (q.topicIds.length > 0) {
      tax.topic = q.topicIds
    }
    if (q.categoryId) {
      tax.category = [q.categoryId]
    }
    if (q.excludeCategoryId) {
      tax['!category'] = [q.excludeCategoryId]
    }
    if (q.tagId) {
      tax.post_tag = [q.tagId]
    }
    return { tax }
  })
  const body = unwrap(
    await getJson<{ data?: { counts?: unknown[] } } | { counts?: unknown[] }>(
      `${apiBase()}/tax_counts?queries=${encodeURIComponent(JSON.stringify(payload))}`
    )
  )
  const counts = body?.counts
  if (!Array.isArray(counts) || counts.length !== queries.length) {
    return null
  }
  return counts.map(c => (typeof c === 'number' ? c : null))
}

/**
 * Posts in one editorial category, site-wide — the Editorial panel's columns.
 *
 * Uses `tax_posts`, NOT `post_list`. Measured on prod 2026-08-01: for category `reviews`
 * both endpoints return the identical posts in the identical order, but `post_list`
 * reports `total_posts: 716` against `tax_posts`' 1,289 — and 1,289 is the number that
 * matches the WordPress term count AND paginates consistently (65 pages of 20). Asked for
 * a page past its own range, `post_list` answers `total_posts: 0`, so its totals move with
 * the page and cannot be shown to a reader.
 */
export async function fetchCategoryPosts(
  categoryId: number | null,
  perPage: number
): Promise<ListResult> {
  if (!categoryId) {
    return EMPTY_LIST
  }
  return fetchTaxPosts({ topicIds: [], categoryId, perPage })
}

/**
 * `per_page` is advisory on both endpoints — they answered with 5 and 10 posts for a
 * requested 3 and 1 — so the cap is enforced here. `total_posts` is still the honest
 * total for the whole query, which is the number the panels display.
 */
function toListResult(raw: RawList | null, limit: number): ListResult {
  if (!raw) {
    return EMPTY_LIST
  }
  return {
    total: typeof raw.total_posts === 'number' ? raw.total_posts : null,
    totalPages: typeof raw.total_pages === 'number' ? raw.total_pages : null,
    stories: (raw.posts ?? []).slice(0, limit).map(toStory),
  }
}

function toStory(post: RawPost): EditorialStory {
  return {
    wpPostId: typeof post.ID === 'number' ? post.ID : null,
    title: post.title,
    // Posts are this app's own routes (`post: '/[slug]'`), so build the link through the
    // shared builder rather than pointing at soundguys.com and forcing a full navigation.
    url: postLinkBuilder(post.slug).href,
    imageUrl: post.image?.src ?? null,
    // `src` is the 2560px original (600KB–1.3MB) — kept for og:image only. The lists
    // render from the sized ladder, WebP variant first.
    imageSrcset: post.image?.webp_srcset ?? post.image?.srcset ?? null,
    excerpt: post.excerpt || null,
    category: post.category,
    author: post.author_name,
    authorUrl: post.author_slug ? authorLinkBuilder(post.author_slug).href : null,
    authorAvatarUrl: post.author_avatar?.src ?? null,
    age: relativeAge(post.date),
    // The raw updated date, verbatim — the freshness key for per-article enrichment
    // caches. `date` here IS the update stamp: WP moves it when an editor edits
    // (verified live: a years-old guide shows its July update, not its publish date).
    updatedAt: post.date ?? null,
    // WP sends 0 for "no score"; the design only badges real ones.
    score: typeof post.score === 'number' && post.score > 0 ? post.score : null,
    badgeUrl: post.badge?.src ?? null,
  }
}

/**
 * "2d ago" from WP's pre-formatted `"July 24, 2025"`.
 *
 * Day-granular on purpose: the payload carries no time-of-day, so the mock's "2h ago" is
 * not derivable. Anything under a day reads "today" rather than inventing an hour count.
 * Falls back to the original string when it can't be parsed — never an empty eyebrow.
 */
export function relativeAge(date: string | null): string | null {
  if (!date) {
    return null
  }
  const then = Date.parse(date)
  if (Number.isNaN(then)) {
    return date
  }
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) {
    return 'today'
  }
  if (days === 1) {
    return '1d ago'
  }
  if (days < 7) {
    return `${days}d ago`
  }
  if (days < 30) {
    return `${Math.floor(days / 7)}w ago`
  }
  if (days < 365) {
    return `${Math.floor(days / 30)}mo ago`
  }
  return date
}
